/**
 * BarChart Component - Bar chart for data comparisons and distributions
 */

import React, { useCallback } from 'react';
import { BaseChart, ChartDataPoint, ChartConfig } from './BaseChart';

export interface BarChartProps extends ChartConfig {
  data: ChartDataPoint[];
  className?: string;
  onDataPointClick?: (dataPoint: ChartDataPoint, index: number) => void;
  onDataPointHover?: (dataPoint: ChartDataPoint | null, index: number) => void;
  loading?: boolean;
  error?: string;
  
  // Bar-specific props
  barWidth?: number;
  barSpacing?: number;
  horizontal?: boolean;
  showValues?: boolean;
  valuePosition?: 'top' | 'center' | 'bottom';
  gradientFill?: boolean;
  roundedCorners?: number;
  multiSeries?: Array<{
    name: string;
    data: ChartDataPoint[];
    color: string;
  }>;
  stacked?: boolean;
}

export const BarChart: React.FC<BarChartProps> = ({
  data,
  className = '',
  onDataPointClick,
  onDataPointHover,
  loading = false,
  error,
  barWidth,
  barSpacing = 0.1,
  horizontal = false,
  showValues = false,
  valuePosition = 'top',
  gradientFill = false,
  roundedCorners = 0,
  multiSeries,
  stacked = false,
  ...configProps
}) => {
  // Use multiSeries if provided, otherwise use single series
  const series = multiSeries || [{ 
    name: 'Data', 
    data, 
    color: configProps.theme?.primary || '#3b82f6'
  }];

  const customRender = useCallback((
    ctx: CanvasRenderingContext2D,
    chartArea: { x: number; y: number; width: number; height: number },
    dataRanges: { xMin: number; xMax: number; yMin: number; yMax: number },
    dataToCanvas: (dataX: number, dataY: number) => { x: number; y: number }
  ) => {
    if (series.length === 0 || series[0].data.length === 0) return;

    const dataLength = series[0].data.length;
    const seriesCount = series.length;
    
    // Calculate bar dimensions
    const availableWidth = horizontal ? chartArea.height : chartArea.width;
    const totalSpacing = (dataLength - 1) * barSpacing * (availableWidth / dataLength);
    const effectiveWidth = availableWidth - totalSpacing;
    
    let calculatedBarWidth: number;
    if (barWidth) {
      calculatedBarWidth = barWidth;
    } else if (stacked) {
      calculatedBarWidth = effectiveWidth / dataLength;
    } else {
      calculatedBarWidth = effectiveWidth / (dataLength * seriesCount);
    }

    // Function to create gradient
    const createGradient = (color: string, x: number, y: number, width: number, height: number) => {
      if (!gradientFill) return color;
      
      const gradient = horizontal 
        ? ctx.createLinearGradient(x, y, x + width, y)
        : ctx.createLinearGradient(x, y + height, x, y);
      
      // Extract RGB values from hex color and create gradient
      const hex = color.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      
      gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.8)`);
      gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 1)`);
      
      return gradient;
    };

    // Function to draw rounded rectangle
    const drawRoundedRect = (x: number, y: number, width: number, height: number, radius: number) => {
      if (radius === 0) {
        ctx.rect(x, y, width, height);
        return;
      }

      const r = Math.min(radius, width / 2, height / 2);
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + width - r, y);
      ctx.quadraticCurveTo(x + width, y, x + width, y + r);
      ctx.lineTo(x + width, y + height - r);
      ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
      ctx.lineTo(x + r, y + height);
      ctx.quadraticCurveTo(x, y + height, x, y + height - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
      ctx.closePath();
    };

    // Draw bars
    series.forEach((seriesData, seriesIndex) => {
      seriesData.data.forEach((dataPoint, dataIndex) => {
        const value = dataPoint.y;
        if (value === 0) return;

        let barX: number, barY: number, barW: number, barH: number;

        if (horizontal) {
          // Horizontal bars
          const zeroPoint = dataToCanvas(0, dataIndex);
          const valuePoint = dataToCanvas(value, dataIndex);
          
          if (stacked) {
            // Calculate stacked position
            let stackedValue = 0;
            for (let i = 0; i < seriesIndex; i++) {
              stackedValue += series[i].data[dataIndex]?.y || 0;
            }
            const stackStart = dataToCanvas(stackedValue, dataIndex);
            const stackEnd = dataToCanvas(stackedValue + value, dataIndex);
            
            barX = Math.min(stackStart.x, stackEnd.x);
            barW = Math.abs(stackEnd.x - stackStart.x);
          } else {
            barX = Math.min(zeroPoint.x, valuePoint.x);
            barW = Math.abs(valuePoint.x - zeroPoint.x);
          }
          
          const barCenter = chartArea.y + (dataIndex + 0.5) * (chartArea.height / dataLength);
          const totalBarHeight = calculatedBarWidth * (stacked ? 1 : seriesCount);
          const seriesOffset = stacked ? 0 : seriesIndex * calculatedBarWidth;
          
          barY = barCenter - totalBarHeight / 2 + seriesOffset;
          barH = calculatedBarWidth;
        } else {
          // Vertical bars
          const zeroPoint = dataToCanvas(dataIndex, 0);
          const valuePoint = dataToCanvas(dataIndex, value);
          
          if (stacked) {
            // Calculate stacked position
            let stackedValue = 0;
            for (let i = 0; i < seriesIndex; i++) {
              stackedValue += series[i].data[dataIndex]?.y || 0;
            }
            const stackStart = dataToCanvas(dataIndex, stackedValue);
            const stackEnd = dataToCanvas(dataIndex, stackedValue + value);
            
            barY = Math.min(stackStart.y, stackEnd.y);
            barH = Math.abs(stackEnd.y - stackStart.y);
          } else {
            barY = Math.min(zeroPoint.y, valuePoint.y);
            barH = Math.abs(valuePoint.y - zeroPoint.y);
          }
          
          const barCenter = chartArea.x + (dataIndex + 0.5) * (chartArea.width / dataLength);
          const totalBarWidth = calculatedBarWidth * (stacked ? 1 : seriesCount);
          const seriesOffset = stacked ? 0 : seriesIndex * calculatedBarWidth;
          
          barX = barCenter - totalBarWidth / 2 + seriesOffset;
          barW = calculatedBarWidth;
        }

        // Draw the bar
        ctx.save();
        const fillStyle = createGradient(seriesData.color, barX, barY, barW, barH);
        ctx.fillStyle = fillStyle;
        
        ctx.beginPath();
        drawRoundedRect(barX, barY, barW, barH, roundedCorners);
        ctx.fill();

        // Add border
        ctx.strokeStyle = seriesData.color;
        ctx.lineWidth = 1;
        ctx.stroke();

        // Draw value labels if enabled
        if (showValues) {
          ctx.fillStyle = '#333';
          ctx.font = '11px sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          
          let labelX: number, labelY: number;
          
          if (horizontal) {
            labelX = valuePosition === 'center' ? barX + barW / 2 : 
                     valuePosition === 'top' ? barX + barW + 5 : barX - 5;
            labelY = barY + barH / 2;
            ctx.textAlign = valuePosition === 'center' ? 'center' : 
                           valuePosition === 'top' ? 'left' : 'right';
          } else {
            labelX = barX + barW / 2;
            labelY = valuePosition === 'center' ? barY + barH / 2 : 
                     valuePosition === 'top' ? barY - 5 : barY + barH + 15;
            ctx.textBaseline = valuePosition === 'center' ? 'middle' : 
                              valuePosition === 'top' ? 'bottom' : 'top';
          }
          
          const displayValue = typeof value === 'number' ?
            (value % 1 === 0 ? value.toString() : value.toFixed(1)) :
            String(value);
          
          ctx.fillText(displayValue, labelX, labelY);
        }

        ctx.restore();
      });
    });
  }, [series, barWidth, barSpacing, horizontal, showValues, valuePosition, gradientFill, roundedCorners, stacked]);

  // For BaseChart compatibility, use the first series data or empty array
  const baseData = series[0]?.data || [];

  return (
    <BaseChart
      data={baseData}
      className={className}
      onDataPointClick={onDataPointClick}
      onDataPointHover={onDataPointHover}
      loading={loading}
      error={error}
      customRender={customRender}
      {...configProps}
    />
  );
};

export default BarChart;